import Avatar from '@/components/base/Avatar';
import StatusBadge from '@/components/base/StatusBadge';
import Button from '@/components/base/Button';
import { formatCompact, formatMoney } from '@/utils/format';
import type { CreatorProfile as Creator } from '@/types/portal';
import { contentTypeOptions } from '../types';
import { matchedFormats } from '../helpers';

interface ShortlistReviewProps {
  creators: Creator[];
  contentTypes: string[];
  rates: Record<string, number>;
  budget: number;
  currency: string;
  onRateChange: (id: string, value: number) => void;
  onRemove: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
}

export default function ShortlistReview({
  creators,
  contentTypes,
  rates,
  budget,
  currency,
  onRateChange,
  onRemove,
  onBack,
  onNext,
}: ShortlistReviewProps) {
  const total = creators.reduce((sum, creator) => sum + (rates[creator.id] ?? creator.rate ?? 0), 0);
  const remaining = budget - total;
  const usage = budget > 0 ? Math.min(100, Math.round((total / budget) * 100)) : 0;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <p className="text-xs text-foreground-500">Creators shortlisted</p>
          <p className="mt-1 font-heading text-2xl font-semibold text-foreground-950">
            {creators.length}
          </p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <p className="text-xs text-foreground-500">Committed spend</p>
          <p className="mt-1 font-heading text-2xl font-semibold text-foreground-950">
            {formatMoney(total, currency)}
          </p>
        </div>
        <div
          className={`rounded-lg border p-4 ${
            remaining < 0 ? 'border-primary-200 bg-primary-50' : 'border-accent-200 bg-accent-50'
          }`}
        >
          <p className={`text-xs ${remaining < 0 ? 'text-primary-700' : 'text-accent-800'}`}>
            {remaining < 0 ? 'Over budget' : 'Budget remaining'}
          </p>
          <p
            className={`mt-1 font-heading text-2xl font-semibold ${
              remaining < 0 ? 'text-primary-800' : 'text-accent-900'
            }`}
          >
            {formatMoney(Math.abs(remaining), currency)}
          </p>
        </div>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between text-xs text-foreground-600">
          <span>Budget usage</span>
          <span className="font-medium text-foreground-900">{usage}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-background-200">
          <div
            className={`h-full rounded-full ${remaining < 0 ? 'bg-primary-500' : 'bg-accent-500'}`}
            style={{ width: `${usage}%` }}
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
        <table className="row-accent w-full min-w-[820px] border-collapse text-left">
          <thead>
            <tr className="border-b border-background-200 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
              <th className="px-5 py-3 font-semibold">Creator</th>
              <th className="px-3 py-3 font-semibold">Reach</th>
              <th className="px-3 py-3 font-semibold">Content formats</th>
              <th className="px-3 py-3 font-semibold">Compliance</th>
              <th className="px-3 py-3 font-semibold">Proposed rate</th>
              <th className="px-5 py-3 text-right font-semibold">Remove</th>
            </tr>
          </thead>
          <tbody>
            {creators.map((creator) => {
              const matched = matchedFormats(creator, contentTypes);
              return (
              <tr key={creator.id} className="border-b border-background-100 last:border-0">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar src={creator.avatar} alt={creator.name} size="sm" />
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{creator.name}</p>
                      <p className="text-xs text-foreground-500">{creator.handle}</p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-sm text-foreground-700">
                  {formatCompact(creator.followers)}
                  <span className="block text-[11px] text-foreground-400">
                    {creator.engagementRate}% eng.
                  </span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex max-w-[240px] flex-wrap gap-1">
                    {matched.map((id) => {
                      const option = contentTypeOptions.find((type) => type.id === id);
                      if (!option) return null;
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 rounded-full border border-accent-300 bg-accent-50 px-2 py-0.5 text-[11px] text-accent-800"
                        >
                          <span aria-hidden="true">{option.emoji}</span>
                          {option.label}
                        </span>
                      );
                    })}
                    {matched.length === 0 ? (
                      <span className="text-xs text-foreground-400">—</span>
                    ) : null}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <StatusBadge status={creator.kycStatus} />
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-foreground-400">{currency}</span>
                    <input
                      type="number"
                      min={0}
                      value={rates[creator.id] ?? creator.rate ?? 0}
                      onChange={(e) => onRateChange(creator.id, Number(e.target.value))}
                      className="h-8 w-28 rounded-md border border-background-300 bg-background-50 px-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
                    />
                  </div>
                </td>
                <td className="px-5 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => onRemove(creator.id)}
                    aria-label={`Remove ${creator.name}`}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 cursor-pointer hover:bg-background-100 hover:text-primary-700"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="outline" icon="ri-arrow-left-line" onClick={onBack}>
          Back to recommendations
        </Button>
        <Button icon="ri-send-plane-line" onClick={onNext} disabled={creators.length === 0}>
          Continue to invite
        </Button>
      </div>
    </div>
  );
}